import React, { useEffect, useRef, useState} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  CircularProgress,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon, RestartAlt as ResetIcon } from '@mui/icons-material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useSocket } from '../contexts/SocketContext.tsx';
import { Call } from '../types/index.ts';

// ─────────────────────────────────────────────────────────────
// Globals provided by your data & style scripts
// ─────────────────────────────────────────────────────────────
declare global {
  interface Window {
    Styles: any;
    colorize: (layer: any) => void;
    data: any[];      // pushed by the data JS files
    buildings: any;   // set by buildings.js (GeoJSON FeatureCollection of points)
  }
}

// ─────────────────────────────────────────────────────────────
// Config (adjust paths if your filenames differ)
// ─────────────────────────────────────────────────────────────
const PLUGINS = [
  { type: 'css', href: '/map-assets/plugins/leaflet.iconlabel.css' },
  { type: 'js', src: '/map-assets/plugins/Leaflet.LabelTextCollision.js' },
  { type: 'js', src: '/map-assets/plugins/Canvas-TextPath.js' },
  { type: 'js', src: '/map-assets/plugins/Leaflet.streetlabels.js' },
  { type: 'js', src: '/map-assets/plugins/leaflet.iconlabel.js' },
  { type: 'js', src: '/map-assets/plugins/styles.js' }, // defines Styles + colorize
] as const;

const DATA_FILES = [
  '/map-assets/data/roads.js',
  '/map-assets/data/jds.js',
  '/map-assets/data/waterways.js',
  '/map-assets/data/tresspassing.js', // ensure this matches your filename
  '/map-assets/data/airspace.js',
];

const BUILDINGS_FILE = '/map-assets/data/buildings.js';

const TILE_OPTIONS = {
  urlTemplate: '/map-assets/tiles/{z}/{y}/{x}.png',
  tileSize: 4096,
  minNativeZoom: 4,
  maxNativeZoom: 4,
  minZoom: 0,
  maxZoom: 6, // keep modest to reduce stutter
  bounds: [[0, 0], [-1010, 1200]] as unknown as L.LatLngBoundsExpression,
  errorTileUrl: '/map-assets/assets/error.png',
} as const;

const MAP_BOUNDS = [[-1010, 0], [0, 1200]] as unknown as L.LatLngBoundsExpression;
const MAP_CENTER: L.LatLngExpression = [-500, 250];
const HIGHLIGHT_MS = 5000;

// ─────────────────────────────────────────────────────────────
// Asset loader – singletons to avoid reloading (race-safe)
// ─────────────────────────────────────────────────────────────
const loadedScripts = new Set<string>();
const loadedCSS = new Set<string>();
let pluginLoadPromise: Promise<void> | null = null;
let dataLoadPromise: Promise<void> | null = null;

function loadCSSOnce(href: string) {
  if (loadedCSS.has(href)) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = href;
    link.onload = () => { loadedCSS.add(href); resolve(); };
    link.onerror = () => reject(new Error(`Failed to load CSS: ${href}`));
    document.head.appendChild(link);
  });
}
function loadScriptOnce(src: string) {
  if (loadedScripts.has(src) || document.querySelector(`script[src="${src}"]`)) {
    loadedScripts.add(src);
    return Promise.resolve();
  }
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src; script.async = true;
    script.onload = () => { loadedScripts.add(src); resolve(); };
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

async function loadPluginsOnce() {
  if (pluginLoadPromise) return pluginLoadPromise;
  pluginLoadPromise = (async () => {
    // Load CSS first
    for (const p of PLUGINS) {
      if (p.type === 'css') await loadCSSOnce(p.href);
    }
    // Then JS plugins in order
    for (const p of PLUGINS) {
      if (p.type === 'js') await loadScriptOnce(p.src);
    }
  })();
  return pluginLoadPromise;
}

async function loadDataOnce() {
  if (dataLoadPromise) return dataLoadPromise;
  dataLoadPromise = (async () => {
    // Ensure array exists; data files push to it
    (window as any).data = [];
    // Load vector datasets
    for (const file of DATA_FILES) {
      const res = await fetch(file);
      if (!res.ok) throw new Error(`${file} -> ${res.status} ${res.statusText}`);
      const js = await res.text();
      const s = document.createElement('script');
      s.textContent = js;
      document.head.appendChild(s);
      document.head.removeChild(s);
    }
    // Load buildings
    const bres = await fetch(BUILDINGS_FILE);
    if (!bres.ok) throw new Error(`${BUILDINGS_FILE} -> ${bres.status} ${bres.statusText}`);
    const bjs = await bres.text();
    const b = document.createElement('script');
    b.textContent = bjs;
    document.head.appendChild(b);
    document.head.removeChild(b);
  })();
  return dataLoadPromise;
}

// ─────────────────────────────────────────────────────────────
// Lightweight fuzzy search (prefix + substring + token matching)
// ─────────────────────────────────────────────────────────────
function normalize(str?: string) {
  return (str || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

function scoreMatch(q: string, candidate: string) {
  // higher is better
  if (!q || !candidate) return 0;
  if (candidate === q) return 100;
  if (candidate.startsWith(q)) return 90;
  if (candidate.includes(q)) return 70;
  // token overlap
  const qTokens = q.split(/\s+/).filter(Boolean);
  const cTokens = candidate.split(/\s+/).filter(Boolean);
  let overlap = 0;
  for (const t of qTokens) if (cTokens.some(c => c.startsWith(t) || c.includes(t))) overlap++;
  return overlap > 0 ? 50 + 5 * overlap : 0;
}

type NameHit<T> = { name: string; norm: string; ref: T; bounds?: L.LatLngBounds; };

interface MapModalProps {
  open: boolean;
  onClose: () => void;
}

// Map bounds configuration - matches Config.MAP from Roblox
// WorldSW = Vector3.new(-6874.011, 0, 6129.688)
// WorldNE = Vector3.new(11779.191, 0, -8783.324)
const WORLD_BOUNDS = {
  SW: { x: -6774.011, z: 6229.688 },
  NE: { x: 11679.191, z: -8883.324 }
};

// Calibration offset - adjust these if markers are slightly off
// Positive values shift right/up, negative values shift left/down
const COORDINATE_OFFSET = {
  lat: 0,  // Adjust vertical position (positive = up, negative = down)
  lng: 0  // Adjust horizontal position (positive = right, negative = left)
};

// Convert Roblox world coordinates to Leaflet lat/lng
// This matches the Roblox worldToUV function implementation
function robloxToLeaflet(robloxPos: { x: number; y: number; z: number }): [number, number] {
  const { SW, NE } = WORLD_BOUNDS;
  
  // Calculate bounds using min/max (handles cases where SW > NE)
  const minX = Math.min(SW.x, NE.x);
  const maxX = Math.max(SW.x, NE.x);
  const minZ = Math.min(SW.z, NE.z);
  const maxZ = Math.max(SW.z, NE.z);
  
  // Calculate normalized UV coordinates (0-1)
  // X coordinate (horizontal on map) - maps to lng
  let u = (robloxPos.x - minX) / Math.max(1e-6, (maxX - minX));
  // Don't invert U (matches Roblox: worldToUV(pos, false, false))
  
  // Z coordinate (vertical on map) - maps to lat
  // Note: In Roblox, Z increases going north, but we need to invert for Leaflet
  // where higher lat values are "up" on the map
  let v = (robloxPos.z - minZ) / Math.max(1e-6, (maxZ - minZ));
  // Invert V because Leaflet's Y-axis is inverted compared to Roblox Z-axis
  v = 1 - v;
  
  // Clamp to valid range
  u = Math.max(0, Math.min(1, u));
  v = Math.max(0, Math.min(1, v));
  
  // Convert to Leaflet coordinates
  // Map bounds: [[-1010, 0], [0, 1200]]
  // Lat: -1010 (bottom/SW) to 0 (top/NE), Lng: 0 (left/SW) to 1200 (right/NE)
  let lat = -1010 + v * 1010; // v=0 (SW after invert) -> lat=-1010, v=1 (NE after invert) -> lat=0
  let lng = u * 1200; // u=0 (SW) -> lng=0, u=1 (NE) -> lng=1200
  
  // Apply calibration offset if needed
  lat += COORDINATE_OFFSET.lat;
  lng += COORDINATE_OFFSET.lng;
  
  return [lat, lng];
}

const MapModal: React.FC<MapModalProps> = ({ open, onClose }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const tileRef = useRef<L.TileLayer | null>(null);
  const layerControlRef = useRef<L.Control.Layers | null>(null);

  const [isBooting, setBooting] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const layerGroupsRef = useRef<Record<string, L.Layer | L.LayerGroup>>({});
  const searchIndexRef = useRef<{
    // name → list of layers/markers
    hits: Array<NameHit<L.Layer | L.Marker>>;
  }>({ hits: [] });

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Array<{ name: string; ref: L.Layer | L.Marker }>>([]);
  const [searchBusy, setSearchBusy] = useState(false);
  
  // Call markers management
  const callMarkersRef = useRef<Map<string, L.Marker>>(new Map());
  const callsLayerRef = useRef<L.LayerGroup | null>(null);
  const socket = useSocket();
  const [calls, setCalls] = useState<Call[]>([]);
  
  // Unit markers management
  const unitMarkersRef = useRef<Map<string, L.Marker>>(new Map());
  const unitsLayerRef = useRef<L.LayerGroup | null>(null);
  const [unitPositions, setUnitPositions] = useState<Map<string, {
    unitId: string;
    position: { x: number; y: number; z: number };
    modelShortname: string | null;
    timestamp: string;
  }>>(new Map());

  // Cleanup
  const teardown = () => {
    try {
      // Clean up call markers
      callMarkersRef.current.forEach(marker => marker.remove());
      callMarkersRef.current.clear();
      if (callsLayerRef.current) {
        callsLayerRef.current.remove();
        callsLayerRef.current = null;
      }
      
      // Clean up unit markers
      unitMarkersRef.current.forEach(marker => marker.remove());
      unitMarkersRef.current.clear();
      if (unitsLayerRef.current) {
        unitsLayerRef.current.remove();
        unitsLayerRef.current = null;
      }
      
      if (mapRef.current) {
        mapRef.current.off();
        mapRef.current.eachLayer(l => {
          // leave tile for GC as well
          (l as any).off?.();
        });
        mapRef.current.remove();
      }
    } catch {}
    mapRef.current = null;
    layerControlRef.current = null;
    tileRef.current = null;
    layerGroupsRef.current = {};
    searchIndexRef.current = { hits: [] };
    setSuggestions([]);
    setUnitPositions(new Map());
  };
  
  // Get color for call code (simplified - you can enhance this)
  const getCallColor = (callCode: string): string => {
    // Priority-based colors
    const colors: Record<string, string> = {
      'CODE': '#ff0000',    // Red for cardiac arrest
      'GSW': '#ff0000',     // Red for gunshot
      'STAB': '#ff0000',    // Red for stabbing
      'OD': '#ff6600',      // Orange for overdose
      'CB': '#ff6600',      // Orange for childbirth
      'DRWN': '#ff6600',    // Orange for drowning
    };
    
    return colors[callCode] || '#ffaa00'; // Default orange
  };
  
  // Get color and icon for unit type
  const getUnitStyle = (modelShortname: string | null): { color: string; icon: string } => {
    const shortname = (modelShortname || '').toUpperCase();
    
    // Unit type colors and icons
    const unitStyles: Record<string, { color: string; icon: string }> = {
      'ENG': { color: '#ff0000', icon: '🚒' },      // Engine - Red
      'LAD': { color: '#ff6600', icon: '🚑' },     // Ladder - Orange
      'AMB': { color: '#00ff00', icon: '🚑' },     // Ambulance - Green
      'BC': { color: '#0000ff', icon: '🚓' },      // Battalion Chief - Blue
      'CAP': { color: '#800080', icon: '🚓' },     // Captain - Purple
      'SUP': { color: '#ff00ff', icon: '🚓' },     // Supervisor - Magenta
      'PD': { color: '#0066ff', icon: '🚓' },      // Police - Blue
      'SHERIFF': { color: '#0066ff', icon: '🚓' }, // Sheriff - Blue
    };
    
    // Try exact match first
    if (unitStyles[shortname]) {
      return unitStyles[shortname];
    }
    
    // Try partial matches
    if (shortname.includes('ENG')) return unitStyles['ENG'];
    if (shortname.includes('LAD')) return unitStyles['LAD'];
    if (shortname.includes('AMB')) return unitStyles['AMB'];
    if (shortname.includes('BC')) return unitStyles['BC'];
    if (shortname.includes('CAP')) return unitStyles['CAP'];
    if (shortname.includes('SUP')) return unitStyles['SUP'];
    if (shortname.includes('PD') || shortname.includes('POLICE')) return unitStyles['PD'];
    if (shortname.includes('SHERIFF')) return unitStyles['SHERIFF'];
    
    // Default
    return { color: '#888888', icon: '🚗' };
  };
  
  // Update call markers on map
  const updateCallMarkers = (callsData: Call[]) => {
    if (!mapRef.current) {
      console.log('[MapModal] Map not ready, skipping marker update');
      return;
    }
    
    console.log('[MapModal] Updating call markers, total calls:', callsData.length);
    
    // Create calls layer if it doesn't exist
    if (!callsLayerRef.current) {
      callsLayerRef.current = L.layerGroup().addTo(mapRef.current);
      console.log('[MapModal] Created calls layer');
    }
    
    const existingCallIds = new Set(callMarkersRef.current.keys());
    const newCallIds = new Set<string>();
    
    // Update or create markers for calls with POI coordinates
    callsData.forEach(call => {
      if (!call.poiCoordinates) {
        console.log(`[MapModal] Call ${call.id} has no POI coordinates, skipping`);
        return;
      }
      
      if (call.status === 'CLOSED') {
        console.log(`[MapModal] Call ${call.id} is CLOSED, skipping`);
        return;
      }
      
      console.log(`[MapModal] Processing call ${call.id} with POI:`, call.poiCoordinates);
      newCallIds.add(call.id);
      
      const [lat, lng] = robloxToLeaflet(call.poiCoordinates);
      console.log(`[MapModal] Converted coordinates: [${lat}, ${lng}]`);
      console.log(`[MapModal] World coords: X=${call.poiCoordinates.x}, Z=${call.poiCoordinates.z}`);
      console.log(`[MapModal] Leaflet coords: Lat=${lat.toFixed(2)}, Lng=${lng.toFixed(2)}`);
      
      // Get or create marker
      let marker = callMarkersRef.current.get(call.id);
      
      if (!marker) {
        console.log(`[MapModal] Creating new marker for call ${call.id} at [${lat}, ${lng}]`);
        // Create new marker with custom icon (square for calls)
        const icon = L.divIcon({
          className: 'call-marker',
          html: `<div style="
            width: 16px;
            height: 16px;
            background-color: ${getCallColor(call.callCode)};
            border: 3px solid #fff;
            border-radius: 3px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.5);
            z-index: 1000;
          "></div>`,
          iconSize: [16, 16],
          iconAnchor: [8, 8]
        });
        
        marker = L.marker([lat, lng], { 
          icon,
          zIndexOffset: 1000 // Ensure markers are on top
        }).addTo(callsLayerRef.current!);
        
        // Add popup with call information
        const popupContent = `
          <div style="font-family: Arial, sans-serif; min-width: 200px;">
            <strong>${call.incidentNumber}</strong><br/>
            <strong>${call.callCode}</strong><br/>
            ${call.location}<br/>
            ${call.poiName ? `<em>${call.poiName}</em><br/>` : ''}
            Priority: ${call.priority}
          </div>
        `;
        marker.bindPopup(popupContent);
        
        callMarkersRef.current.set(call.id, marker);
        console.log(`[MapModal] Marker created and added for call ${call.id}`);
      } else {
        // Update existing marker position
        marker.setLatLng([lat, lng]);
        
        // Update popup content
        const popupContent = `
          <div style="font-family: Arial, sans-serif; min-width: 200px;">
            <strong>${call.incidentNumber}</strong><br/>
            <strong>${call.callCode}</strong><br/>
            ${call.location}<br/>
            ${call.poiName ? `<em>${call.poiName}</em><br/>` : ''}
            Priority: ${call.priority}
          </div>
        `;
        marker.setPopupContent(popupContent);
      }
    });
    
    // Remove markers for calls that no longer exist or are closed
    existingCallIds.forEach(callId => {
      if (!newCallIds.has(callId)) {
        const marker = callMarkersRef.current.get(callId);
        if (marker) {
          marker.remove();
          callMarkersRef.current.delete(callId);
        }
      }
    });
  };
  
  // Update unit markers on map
  const updateUnitMarkers = () => {
    if (!mapRef.current) {
      console.log('[MapModal] Map not ready, skipping unit marker update');
      return;
    }
    
    console.log('[MapModal] updateUnitMarkers called with', unitPositions.size, 'unit positions');
    
    // Create units layer if it doesn't exist
    if (!unitsLayerRef.current) {
      unitsLayerRef.current = L.layerGroup().addTo(mapRef.current);
      console.log('[MapModal] Created units layer');
    }
    
    const existingUnitIds = new Set(unitMarkersRef.current.keys());
    const newUnitIds = new Set<string>();
    
    // Update or create markers for units with positions
    unitPositions.forEach((unitData, unitId) => {
      newUnitIds.add(unitId);
      
      console.log(`[MapModal] Processing unit ${unitId} at position:`, unitData.position);
      const [lat, lng] = robloxToLeaflet(unitData.position);
      console.log(`[MapModal] Converted to Leaflet coords: [${lat.toFixed(2)}, ${lng.toFixed(2)}]`);
      
      // Get or create marker
      let marker = unitMarkersRef.current.get(unitId);
      const style = getUnitStyle(unitData.modelShortname);
      console.log(`[MapModal] Unit ${unitId} style:`, style);
      
      if (!marker) {
        console.log(`[MapModal] Creating new unit marker for ${unitId} at [${lat}, ${lng}]`);
        // Create new marker with custom icon (circle for units)
        const icon = L.divIcon({
          className: 'unit-marker',
          html: `<div style="
            width: 20px;
            height: 20px;
            background-color: ${style.color};
            border: 3px solid #fff;
            border-radius: 50%;
            box-shadow: 0 2px 8px rgba(0,0,0,0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            z-index: 1001;
          ">${style.icon}</div>`,
          iconSize: [20, 20],
          iconAnchor: [10, 10]
        });
        
        marker = L.marker([lat, lng], { 
          icon,
          zIndexOffset: 1001 // Ensure unit markers are above call markers
        }).addTo(unitsLayerRef.current!);
        
        // Add popup with unit information
        const popupContent = `
          <div style="font-family: Arial, sans-serif; min-width: 200px;">
            <strong>Unit: ${unitId}</strong><br/>
            ${unitData.modelShortname ? `Type: ${unitData.modelShortname}<br/>` : ''}
            Position: ${unitData.position.x.toFixed(1)}, ${unitData.position.z.toFixed(1)}
          </div>
        `;
        marker.bindPopup(popupContent);
        
        unitMarkersRef.current.set(unitId, marker);
        console.log(`[MapModal] Unit marker created and added for ${unitId}`);
      } else {
        // Update existing marker position
        marker.setLatLng([lat, lng]);
        
        // Update icon if type changed
        const currentStyle = getUnitStyle(unitData.modelShortname);
        const newIcon = L.divIcon({
          className: 'unit-marker',
          html: `<div style="
            width: 20px;
            height: 20px;
            background-color: ${currentStyle.color};
            border: 3px solid #fff;
            border-radius: 50%;
            box-shadow: 0 2px 8px rgba(0,0,0,0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            z-index: 1001;
          ">${currentStyle.icon}</div>`,
          iconSize: [20, 20],
          iconAnchor: [10, 10]
        });
        marker.setIcon(newIcon);
        
        // Update popup content
        const popupContent = `
          <div style="font-family: Arial, sans-serif; min-width: 200px;">
            <strong>Unit: ${unitId}</strong><br/>
            ${unitData.modelShortname ? `Type: ${unitData.modelShortname}<br/>` : ''}
            Position: ${unitData.position.x.toFixed(1)}, ${unitData.position.z.toFixed(1)}
          </div>
        `;
        marker.setPopupContent(popupContent);
      }
    });
    
    // Remove markers for units that no longer have positions
    existingUnitIds.forEach(unitId => {
      if (!newUnitIds.has(unitId)) {
        const marker = unitMarkersRef.current.get(unitId);
        if (marker) {
          marker.remove();
          unitMarkersRef.current.delete(unitId);
        }
      }
    });
  };

  // Boot sequence
  useEffect(() => {
    let cancelled = false;

    async function boot() {
      if (!open) return;
      setBooting(true);
      setErr(null);

      try {
        // Load plugins and styles
        await loadPluginsOnce();
        // Load datasets (window.data + window.buildings)
        await loadDataOnce();

        if (cancelled) return;
        if (!containerRef.current) throw new Error('Map container missing');

        // Build layers
        const datasets = window.data || [];
        const groups: Record<string, L.Layer | L.LayerGroup> = {};
        const hits: Array<NameHit<L.Layer | L.Marker>> = [];

        // Create renderers/options
        const useCanvas = true;
        const canvasRenderer = useCanvas ? L.canvas({ padding: 0.3 }) : undefined;

        // Optional street labels renderer (collision)
let streetLabelsRenderer: any = null;
try {
  streetLabelsRenderer = new (L as any).StreetLabels({
    collisionFlg: true,
    propertyName: 'name',
    showLabelIf: () => true,
    fontStyle: {
      dynamicFontSize: false,
      fontSize: 14,
      fontSizeUnit: 'px',
      lineWidth: 2,
      fillStyle: 'white',
      strokeStyle: 'black',
    },
  });
} catch {
  streetLabelsRenderer = null;
}
// Helper: apply a renderer to all Path children (post-creation)
const applyRendererToGroup = (group: L.Layer, renderer: any) => {
    if (!renderer) return;
    if ((group as any).eachLayer) {
      (group as any).eachLayer((lay: any) => {
        if (lay && typeof lay.setStyle === 'function') {
          // Path-like layer
          lay.options = { ...(lay.options || {}), renderer };
          // Some layers need a re-style to bind renderer
          try { lay.redraw?.(); } catch {}
        }
      });
    }
  };
        const onEachGeneric = (feature: any, layer: any) => {
          try {
            if (window.colorize) window.colorize(layer);
          } catch {}
          (layer as any).feature = feature;
          const nm = feature?.properties?.name;
          if (nm) {
            const b = (layer.getBounds?.() as L.LatLngBounds | undefined) ||
                      (layer.getLatLng?.() ? L.latLngBounds(layer.getLatLng(), layer.getLatLng()) : undefined);
            hits.push({ name: nm, norm: normalize(nm), ref: layer, bounds: b });
          }
        };

        const onEachRoad = (feature: any, layer: any) => {
          onEachGeneric(feature, layer);
        };

        // Build vector groups
        for (const json of datasets) {
          const layerName = json?.properties?.layerName as string | undefined;
          if (!layerName) continue;

          const isRoads = layerName === 'Roads';
          const group = L.geoJSON(json as any, {
            onEachFeature: isRoads ? onEachRoad : onEachGeneric,
            // no 'renderer' here – avoids TS error
          } as L.GeoJSONOptions<any>);
          
          // Apply renderers post-creation (type-safe)
          applyRendererToGroup(group, canvasRenderer);
          applyRendererToGroup(group, streetLabelsRenderer);

          if (streetLabelsRenderer && (group as any).options) {
            (group as any).options.renderer = streetLabelsRenderer;
          }

          groups[layerName] = group;
        }

        // Buildings: icon-label if present, else divIcon fallback
        const buildingsFC = window.buildings;
        if (buildingsFC?.features?.length) {
          const markers: L.Marker[] = [];

          let MarkerLabel: any = null;
          let IconLabel: any = null;
          try {
            IconLabel = (L as any).Icon.Label.extend({
              options: {
                iconSize: new L.Point(44, 44),
                shadowSize: [0, 0],
                popupAnchor: [-3, -76],
                wrapperAnchor: new L.Point(22, 44),
                iconAnchor: new L.Point(0, 0),
                labelAnchor: new L.Point(32, 9),
              },
            });
            MarkerLabel = (L as any).Marker.Label;
          } catch {
            IconLabel = null;
            MarkerLabel = null;
          }

          const makeMarker = (iconName: string, labelText: string) => {
            if (!IconLabel || !MarkerLabel) {
              return L.marker([0, 0], {
                icon: L.divIcon({
                  className: 'bldg-fallback',
                  html: `<div style="width:14px;height:14px;border-radius:50%;background:#e74c3c;"></div>`,
                  iconSize: [14, 14],
                }),
              });
            }
            const icon = new IconLabel({
              iconUrl: `/map-assets/assets/markers/${iconName || 'default'}.png`,
              labelText,
            });
            return new MarkerLabel([0, 0], { icon });
          };

          for (const f of buildingsFC.features) {
            const lat = f?.geometry?.coordinates?.[1];
            const lon = f?.geometry?.coordinates?.[0];
            if (lat == null || lon == null) continue;
            const nm = f?.properties?.name || '';
            const iconName = f?.properties?.style || 'default';
            const m = makeMarker(iconName, nm);
            m.setLatLng([lat, lon]);
            (m as any).feature = f;
            markers.push(m);

            const b = L.latLngBounds([lat, lon], [lat, lon]);
            if (nm) hits.push({ name: nm, norm: normalize(nm), ref: m, bounds: b });
          }

          groups['Buildings'] = L.layerGroup(markers);
        }

        // Initialize map
        const map = L.map(containerRef.current, {
          crs: L.CRS.Simple,
          center: MAP_CENTER,
          zoom: 1,
          maxBounds: MAP_BOUNDS,
          maxBoundsViscosity: 1.0,
          preferCanvas: true,
          scrollWheelZoom: true,
          zoomAnimation: true,          // avoid stutter on large tiles
          fadeAnimation: true,          // avoid jank on heavy layers
          markerZoomAnimation: false,
          wheelDebounceTime: 50,
          wheelPxPerZoomLevel: 80,
        });

        L.DomEvent.disableScrollPropagation(map.getContainer());
        L.DomEvent.disableClickPropagation(map.getContainer());

        const tile = L.tileLayer(TILE_OPTIONS.urlTemplate, {
          tileSize: TILE_OPTIONS.tileSize,
          minNativeZoom: TILE_OPTIONS.minNativeZoom,
          maxNativeZoom: TILE_OPTIONS.maxNativeZoom,
          minZoom: TILE_OPTIONS.minZoom,
          maxZoom: TILE_OPTIONS.maxZoom,
          bounds: TILE_OPTIONS.bounds,
          updateWhenIdle: true,     // draw when idle (keeps CPU low)
          updateWhenZooming: false, // don't tear tiles mid-zoom
        keepBuffer: 3,            // keep a couple rings of tiles, reduces flashing       // reuse <img> elements instead of recreatin
        
        updateInterval: 300,
          errorTileUrl: TILE_OPTIONS.errorTileUrl,
          crossOrigin: 'anonymous',
        }).addTo(map);

        // Add groups based on toggles
        const overlayMap: Record<string, L.Layer> = {};
        for (const [name, group] of Object.entries(groups)) {
          overlayMap[name] = group as L.Layer;
           if (name === 'Roads') {
               (group as L.Layer).addTo(map);
             }
        }

        // Optional labels renderer
        try { streetLabelsRenderer?.addTo?.(map); } catch {}

        const lc = L.control.layers({ Base: tile }, overlayMap, { collapsed: true }).addTo(map);

        // Assign refs
        mapRef.current = map;
        tileRef.current = tile;
        layerControlRef.current = lc;
        layerGroupsRef.current = groups;
        searchIndexRef.current = { hits };

        setBooting(false);
      } catch (e: any) {
        if (!cancelled) {
          setErr(e?.message || 'Failed to load map');
          setBooting(false);
        }
      }
    }

    if (open && !mapRef.current) boot();

    return () => {
      cancelled = true;
      if (!open) {
        // Fully teardown when dialog closes
        teardown();
        setBooting(true);
        setErr(null);
        setQuery('');
        setSuggestions([]);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // boot once per open

  // Fetch and listen for call updates
  useEffect(() => {
    if (!open || !socket) return;
    
    // Fetch initial calls
    const fetchCalls = async () => {
      try {
        console.log('[MapModal] Fetching active calls...');
        const response = await fetch('/api/calls/active');
        if (response.ok) {
          const data = await response.json();
          console.log('[MapModal] Received calls data:', data);
          if (data.success && data.data) {
            console.log('[MapModal] Setting calls:', data.data.length, 'calls');
            console.log('[MapModal] Calls with POI coordinates:', 
              data.data.filter((c: Call) => c.poiCoordinates).length);
            setCalls(data.data);
          }
        } else {
          console.error('[MapModal] Failed to fetch calls:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('[MapModal] Error fetching calls:', error);
      }
    };
    
    fetchCalls();
    
    // Listen for call updates
    const handleCallCreated = (data: any) => {
      setCalls(prev => {
        const existing = prev.find(c => c.id === data.call?.id);
        if (existing) return prev;
        return [...prev, data.call].filter(c => c.status !== 'CLOSED');
      });
    };
    
    const handleCallUpdated = (data: any) => {
      setCalls(prev => prev.map(c => 
        c.id === data.call?.id ? data.call : c
      ).filter(c => c.status !== 'CLOSED'));
    };
    
    const handleStateUpdate = (data: any) => {
      if (data.calls) {
        setCalls(data.calls.filter((c: Call) => c.status !== 'CLOSED'));
      }
    };
    
    socket.on('call-created', handleCallCreated);
    socket.on('call-updated', handleCallUpdated);
    socket.on('state-update', handleStateUpdate);
    
    return () => {
      socket.off('call-created', handleCallCreated);
      socket.off('call-updated', handleCallUpdated);
      socket.off('state-update', handleStateUpdate);
    };
  }, [open, socket]);
  
  // Update markers when calls change
  useEffect(() => {
    if (mapRef.current && !isBooting) {
      updateCallMarkers(calls);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calls, isBooting]);
  
  // Update unit markers when positions change
  useEffect(() => {
    console.log('[MapModal] unitPositions changed, size:', unitPositions.size, 'map ready:', !!mapRef.current, 'booting:', isBooting);
    if (mapRef.current && !isBooting) {
      console.log('[MapModal] Calling updateUnitMarkers with', unitPositions.size, 'units');
      updateUnitMarkers();
    } else {
      console.log('[MapModal] Skipping updateUnitMarkers - map not ready or still booting');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitPositions, isBooting]);
  
  // Listen for unit position updates via websocket
  useEffect(() => {
    if (!open || !socket) {
      console.log('[MapModal] Skipping unit position listener setup - open:', open, 'socket:', !!socket);
      return;
    }
    
    console.log('[MapModal] Setting up unit position update listener, socket connected:', socket.connected);
    
    // Ensure we're in the dispatch room to receive broadcasts
    if (socket.connected) {
      console.log('[MapModal] Joining dispatch room to receive unit position updates');
      socket.emit('join-dispatch');
    }
    
    const handleConnect = () => {
      console.log('[MapModal] Socket connected, joining dispatch room');
      socket.emit('join-dispatch');
    };
    
    socket.on('connect', handleConnect);
    
    const handleUnitPositionUpdate = (data: {
      unitId: string;
      position: { x: number; y: number; z: number };
      modelShortname: string | null;
      timestamp: string;
    }) => {
      console.log('[MapModal] ✅ Received unit position update:', data);
      console.log('[MapModal] Map ready:', !!mapRef.current, 'Booting:', isBooting);
      setUnitPositions(prev => {
        const next = new Map(prev);
        next.set(data.unitId, data);
        console.log('[MapModal] Updated unitPositions, total units:', next.size, 'unitIds:', Array.from(next.keys()));
        return next;
      });
    };
    
    socket.on('unit-position-update', handleUnitPositionUpdate);
    console.log('[MapModal] Unit position update listener registered');
    
    return () => {
      console.log('[MapModal] Cleaning up unit position update listener');
      socket.off('unit-position-update', handleUnitPositionUpdate);
      socket.off('connect', handleConnect);
    };
  }, [open, socket, isBooting]);


  // Search suggestions (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      const q = normalize(query);
      if (!q) { setSuggestions([]); return; }
      setSearchBusy(true);
      const hits = searchIndexRef.current.hits;
      const ranked = hits
        .map(h => ({ h, s: scoreMatch(q, h.norm) }))
        .filter(x => x.s > 0)
        .sort((a, b) => b.s - a.s)
        .slice(0, 12)
        .map(({ h }) => ({ name: h.name, ref: h.ref }));
      setSuggestions(ranked);
      setSearchBusy(false);
    }, 120);
    return () => clearTimeout(t);
  }, [query]);

  // Highlight helper
  const highlightLayer = (layer: L.Layer | L.Marker) => {
    const map = mapRef.current; if (!map) return;

    // Fit bounds if available; else pan to marker
    let b: L.LatLngBounds | null = null;
    if ((layer as any).getBounds) {
      try { b = (layer as any).getBounds() as L.LatLngBounds; } catch {}
    }
    if (!b && (layer as any).getLatLng) {
      const ll = (layer as any).getLatLng() as L.LatLng;
      b = L.latLngBounds(ll, ll).pad(0.01);
    }
    if (b) map.fitBounds(b, { maxZoom: 4, animate: false });

    // Style pulse (vectors)
    const orig: any = {};
    const asAny = layer as any;
    const canStyle = typeof asAny.setStyle === 'function';

    if (canStyle && window.Styles?.Hover) {
      try {
        // capture minimal original properties
        orig.__colorized = true;
        asAny.setStyle(window.Styles.Hover);
        (asAny.redraw?.());
        setTimeout(() => {
          try {
            if (window.colorize) window.colorize(asAny);
          } catch {}
        }, HIGHLIGHT_MS);
      } catch {}
    }

    // Simple marker bounce-ish feedback (if marker)
    if ((layer as any).setZIndexOffset) {
      try {
        (layer as any).setZIndexOffset(9999);
        setTimeout(() => (layer as any).setZIndexOffset(0), HIGHLIGHT_MS);
      } catch {}
    }
  };

  const runSearch = (name?: string) => {
    const q = normalize(name || query);
    if (!q) return;
    const hits = searchIndexRef.current.hits;
    // choose top result
    const best = hits
      .map(h => ({ h, s: scoreMatch(q, h.norm) }))
      .filter(x => x.s > 0)
      .sort((a, b) => b.s - a.s)[0]?.h;
    if (!best) return;
    highlightLayer(best.ref);
  };

  const onSuggestionClick = (s: { name: string; ref: L.Layer | L.Marker }) => {
    setQuery(s.name);
    highlightLayer(s.ref);
    setSuggestions([]);
  };

  const handleClose = () => {
    onClose();
  };

  const resetView = () => {
    const map = mapRef.current; if (!map) return;
    map.setView(MAP_CENTER as any, 1, { animate: false });
  };

  return (
    <>
      <style>
        {`
          .leaflet-container {
            background: #46604a !important;
            outline-offset: 1px;
          }
        `}
      </style>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '95vw',
            height: '95vh',
            maxWidth: 'none',
            maxHeight: 'none',
            backgroundColor: '#46604a',
            border: '1px solid #40444b',
            borderRadius: '12px',
          },
        }}
      >
      <DialogTitle
        sx={{
          color: '#eceef1',
          fontSize: '22px',
          fontWeight: 'bold',
          borderBottom: '1px solid #40444b',
          p: '12px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
        }}
      >
        Firestone Map
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Reset view">
            <IconButton onClick={resetView} sx={{ color: '#eceef1' }}>
              <ResetIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={handleClose} sx={{ color: '#eceef1' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, position: 'relative', height: '100%', overflow: 'hidden' }}>
        {/* Left controls: search + layer toggles */}
        <Paper
          elevation={0}
          sx={{
            position: 'absolute',
            top: 11,
            left: 50,
            zIndex: 1000,
            p: 1.25,
            background: 'rgba(28,31,37,0.92)',
            border: '1px solid #40444b',
            borderRadius: 1,
            width: 360,
            maxHeight: '80%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search roads or buildings"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#eceef1' }} />
                  </InputAdornment>
                ),
                sx: {
                  color: '#eceef1',
                  '& .MuiOutlinedInput-root': {
                    color: '#eceef1',
                    '& fieldset': { borderColor: '#40444b' },
                    '&:hover fieldset': { borderColor: '#eceef1' },
                    '&.Mui-focused fieldset': { borderColor: '#eceef1' },
                  },
                  '& .MuiInputBase-input::placeholder': { color: '#eceef1', opacity: 0.7 },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={() => runSearch()}
              disabled={searchBusy}
              sx={{ whiteSpace: 'nowrap', backgroundColor: '#3498db', '&:hover': { backgroundColor: '#2980b9' } }}
            >
              {searchBusy ? '...' : 'Search'}
            </Button>
          </Box>

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                border: '1px solid #40444b',
                background: 'rgba(22,24,29,0.96)',
                borderRadius: 1,
                maxHeight: 220,
                overflowY: 'auto',
              }}
            >
              <List dense disablePadding>
                {suggestions.map((s, i) => (
                  <React.Fragment key={`${s.name}-${i}`}>
                    {i > 0 && <Divider sx={{ borderColor: '#2c2f36' }} />}
                    <ListItemButton onClick={() => onSuggestionClick(s)}>
                      <ListItemText
                        primary={s.name}
                        primaryTypographyProps={{ sx: { color: '#eceef1', fontSize: 14 } }}
                      />
                    </ListItemButton>
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          )}

          <Divider sx={{ borderColor: '#2c2f36', my: 0.5 }} />
        </Paper>

        {/* Map container */}
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            height: '100%',
            backgroundColor: '#46604a',
            position: 'relative',
             // prevent browser/page from treating wheel/touch as scroll gestures:
   overscrollBehavior: 'contain',
 touchAction: 'none',
          }}
        />

        {/* Loading overlay */}
        {isBooting && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(28, 31, 37, 0.8)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1001,
            }}
          >
            <CircularProgress sx={{ color: '#3498db', mb: 2 }} />
            <Typography sx={{ color: '#eceef1', mb: 1 }}>Loading Firestone Map…</Typography>
            <Typography sx={{ color: '#eceef1', fontSize: '12px', opacity: 0.7 }}>
              If it takes too long, check the browser console for asset errors.
            </Typography>
          </Box>
        )}

        {/* Error overlay */}
        {err && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(28, 31, 37, 0.9)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1001,
              gap: 2,
              p: 2,
            }}
          >
            <Typography sx={{ color: '#e74c3c', textAlign: 'center' }}>{err}</Typography>
            <Button
              variant="contained"
              onClick={() => {
                // force reboot
                teardown();
                setBooting(true);
                setErr(null);
                // open effect will re-run boot since mapRef is null
                setTimeout(() => {
                  if (open && !mapRef.current) {
                    // noop, effect triggers automatically
                  }
                }, 0);
              }}
              sx={{ backgroundColor: '#3498db', '&:hover': { backgroundColor: '#2980b9' } }}
            >
              Retry
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default MapModal;
